import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { HeroFull } from '@/components/HeroFull';
import { HERO_IMAGE_DEFAULT } from '@/config/images';
import Header from "../../components/feature/Header";
import Breadcrumbs from "../../components/feature/Breadcrumbs";
import Footer from "../../components/feature/Footer";
import WhatsAppFloating from "../../components/feature/WhatsAppFloating";
import CookieBanner from "../../components/feature/CookieBanner";
import QuoteFormSection from "../home/components/QuoteFormSection";
import SocialProofSection from "../../components/feature/SocialProofSection";
import SEO from "../../components/feature/SEO";

const motivos = [
  { emoji: "🦟", title: "Protección contra mosquitos del Mediterráneo", desc: "Málaga tiene especies activas desde abril hasta noviembre. Una mosquitera es tu primera línea de defensa." },
  { emoji: "🌬️", title: "Ventila tu casa sin insectos", desc: "Abre las ventanas de noche y deja que entre la brisa fresca del mar. Sin preocuparte por mosquitos ni polen." },
  { emoji: "🌅", title: "Ideal para noches cálidas de verano", desc: "En verano en Málaga hace demasiado calor para cerrar las ventanas. Con mosquiteras duermes fresco y protegido." },
  { emoji: "🛡️", title: "Mosquito tigre y otros insectos invasivos", desc: "El mosquito tigre y el mosquito común son cada vez más agresivos. La mosquitera te protege a ti y a los peques." },
];

const servicios = [
  {
    title: "Mosquiteras enrollables",
    desc: "Se enrollan cuando no las necesitas. Discretas, prácticas y resistentes al viento. Compatibles con persianas.",
    to: "/mosquiteras/mosquiteras-enrollables",
    icon: "ri-arrow-up-down-line",
    rotation: "-rotate-[0.5deg]",
    linkText: "Ver mosquitera enrollable →",
  },
  {
    title: "Mosquiteras correderas",
    desc: "Para puertas y ventanales grandes. Sistema de raíles suave que no ocupa espacio cuando está abierta.",
    to: "/mosquiteras/mosquiteras-correderas",
    icon: "ri-arrow-left-right-line",
    rotation: "rotate-[0.3deg]",
    linkText: "Ver mosquitera corredera →",
  },
  {
    title: "Mosquiteras fijas",
    desc: "Marco con tela fija. La opción más económica y sencilla para ventanas pequeñas. Desde 60€.",
    to: "/mosquiteras/mosquiteras-fijas",
    icon: "ri-layout-4-line",
    rotation: "-rotate-[0.3deg]",
    linkText: "Ver mosquitera fija →",
  },
  {
    title: "Reparación de mosquiteras",
    desc: "Telas rotas, marcos descuadrados o sistemas atascados. Cambiamos la tela en minutos y ajustamos el marco.",
    to: "/mosquiteras/reparacion-mosquiteras",
    icon: "ri-tools-line",
    rotation: "rotate-[0.5deg]",
    linkText: "Reparar mi mosquitera →",
  },
];

const precios = [
  { tipo: "Mosquitera fija", precio: "desde 60 €", emoji: "📌" },
  { tipo: "Mosquitera corredera", precio: "desde 90 €", emoji: "🚪" },
  { tipo: "Mosquitera enrollable", precio: "desde 120 €", emoji: "🌀" },
  { tipo: "Reparación tela rota", precio: "35-60 €", emoji: "🔧" },
];

const trust = [
  { emoji: "🏆", title: "+10 años · desde 2015", desc: "Instalando mosquiteras en Málaga y toda la Costa del Sol desde 2015." },
  { emoji: "🔧", title: "Técnicos propios", desc: "Nuestro equipo mide, fabrica e instala. Sin subcontratas ni intermediarios." },
  { emoji: "🛡️", title: "Seguro RC", desc: "Seguro de Responsabilidad Civil para tu tranquilidad total." },
  { emoji: "✅", title: "Garantía 2 años", desc: "En marcos, telas y mano de obra. Si algo falla, venimos gratis." },
];

const zonas = [
  { name: "Málaga", slug: "malaga" },
  { name: "Marbella", slug: "marbella" },
  { name: "Fuengirola", slug: "fuengirola" },
  { name: "Torremolinos", slug: "torremolinos" },
  { name: "Benalmádena", slug: "benalmadena" },
  { name: "Mijas", slug: "mijas" },
  { name: "Estepona", slug: "estepona" },
];

export default function Mosquiteras() {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        ogType="website"
        breadcrumbs={[{ name: "Inicio", path: "/" }, { name: "Mosquiteras" }]}
      />
      <Header />
      <Breadcrumbs />
      <main>
        <HeroFull
          imageSrc={HERO_IMAGE_DEFAULT}
          imageAlt="Mosquiteras a medida en Málaga y Costa del Sol"
          badge="⚡ 24h · Respuesta en 20 min · Málaga"
          title="Mosquiteras en Málaga: verano sin mosquitos desde 60€"
          subtitle="Enrollables, correderas y fijas a medida. Instalación en menos de 24h."
          primaryCta={{ href: "tel:+34951120770", label: "Llamar: 951 12 07 70" }}
          secondaryCta={{ href: "#presupuesto", label: "Pedir presupuesto" }}
          trustSignals={["+10 años · desde 2015", "Seguro RC", "Garantía 2 años", "Atendemos toda la Costa del Sol"]}
        />

        <SocialProofSection />

        {/* ¿POR QUÉ? */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">¿Por qué necesitas mosquiteras en Málaga?</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {motivos.map((m, i) => (
                <motion.div
                  key={m.title}
                  className="bg-white rounded-[20px] p-8 text-center shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-4xl mb-4">{m.emoji}</div>
                  <h3 className="font-heading font-bold text-lg text-text-primary mb-2">{m.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{m.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SERVICIOS GRID */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Nuestros servicios de mosquiteras</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {servicios.map((s, i) => (
                <motion.div
                  key={s.title}
                  className={`bg-surface-alt rounded-[20px] p-8 ${s.rotation} border-t-[6px] border-secondary shadow-sm hover:shadow-md transition-shadow`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-12 h-12 bg-secondary/10 text-secondary rounded-xl flex items-center justify-center mb-5">
                    <i className={`${s.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{s.title}</h3>
                  <p className="font-body text-sm text-text-secondary mb-4 leading-relaxed">{s.desc}</p>
                  <Link to={s.to} className="inline-flex items-center gap-1 font-body font-semibold text-sm text-secondary hover:text-secondary-dark underline underline-offset-4 transition-colors">
                    {s.linkText}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* PRECIOS */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Precios orientativos de mosquiteras</h2>
              <div className="w-10 h-1 bg-primary mx-auto rounded-full"></div>
              <p className="font-body text-lg text-text-secondary mt-4 max-w-2xl mx-auto">
                Precios desde 60€. Cada mosquitera se fabrica a medida para tu ventana o puerta.
              </p>
            </div>
            <div className="max-w-2xl mx-auto bg-white rounded-3xl overflow-hidden shadow-sm">
              {precios.map((p, i) => (
                <div
                  key={p.tipo}
                  className={`flex items-center justify-between px-6 md:px-8 py-5 ${i !== precios.length - 1 ? "border-b border-border" : ""}`}
                >
                  <span className="flex items-center gap-3 font-body text-base md:text-lg text-text-primary">
                    <span>{p.emoji}</span> {p.tipo}
                  </span>
                  <span className="font-heading font-bold text-lg md:text-xl text-primary">{p.precio}</span>
                </div>
              ))}
            </div>
            <p className="text-center font-body text-sm text-text-secondary mt-6">
              Precios orientativos según dimensiones y acabados. Presupuesto exacto sin compromiso tras visita técnica.
            </p>
          </div>
        </section>

        {/* ZONAS */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">Instalamos mosquiteras en toda la Costa del Sol</h2>
              <p className="font-body text-lg text-text-secondary max-w-2xl mx-auto">
                Te visitamos en menos de 24h en cualquiera de estas zonas.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 md:gap-4">
              {zonas.map((z) => (
                <Link
                  key={z.name}
                  to={`/zonas/${z.slug}`}
                  className="inline-flex items-center px-6 py-3 bg-surface-alt border border-border rounded-pill font-body font-semibold text-base text-text-primary hover:bg-secondary hover:text-white hover:border-secondary transition-all"
                >
                  {z.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* TRUST IMAGE */}
        <section className="bg-white py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6 text-center">
            <div className="max-w-2xl mx-auto">
              <img
                src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=600&q=80&auto=format&fit=crop"
                alt="Técnico profesional instalando mosquiteras en Málaga"
                className="w-full h-auto rounded-xl object-cover aspect-[16/9]"
                loading="lazy"
                width="600"
                height="338"
              />
            </div>
          </div>
        </section>

        {/* TRUST */}
        <section className="bg-surface-alt py-16 md:py-24">
          <div className="max-w-container mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {trust.map((t, i) => (
                <motion.div
                  key={t.title}
                  className="bg-white rounded-[20px] p-8 shadow-sm"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="text-3xl mb-3">{t.emoji}</div>
                  <h3 className="font-heading font-bold text-xl text-text-primary mb-2">{t.title}</h3>
                  <p className="font-body text-sm text-text-secondary leading-relaxed">{t.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== SECCIÓN VIDEODIAGNÓSTICO — 2 COL: texto IZQ, imagen DER, fondo amarillo ===== */}
        <section className="py-16 md:py-24" style={{ backgroundColor: "#ECFCCB" }}>
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
              {/* COLUMNA IZQUIERDA — Texto */}
              <motion.div
                className="flex flex-col gap-6 md:order-1"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <p className="font-body font-bold text-xs tracking-widest uppercase" style={{ color: "#D97706" }}>
                  📹 NOVEDAD
                </p>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary leading-tight">
                  Diagnóstico GRATIS por videollamada WhatsApp
                </h2>
                <p className="font-body text-lg text-text-secondary leading-relaxed">
                  ¿No estás seguro de qué mosquitera necesitas? Muéstranos tu ventana o puerta por videollamada y te asesoramos <strong>GRATIS</strong>. Sin compromiso, sin desplazamiento, en menos de 20 minutos.
                </p>
                <ul className="flex flex-col gap-3">
                  {["Diagnóstico instantáneo", "Presupuesto orientativo al momento", "Ahorra la visita técnica si es sencillo", "Disponible L-V 9-19h, S 9-14h"].map((b) => (
                    <li key={b} className="flex items-center gap-3 font-body text-sm text-text-primary">
                      <span className="w-5 h-5 shrink-0 flex items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs" aria-hidden="true">✓</span>
                      {b}
                    </li>
                  ))}
                </ul>
                <a
                  href="https://wa.me/34627213347?text=Hola%2C%20quiero%20un%20videodiagn%C3%B3stico%20de%20mosquitera"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-whatsapp hover:bg-[#128C7E] text-white font-body font-bold text-xl px-8 py-5 rounded-pill transition-colors whitespace-nowrap w-fit"
                >
                  <span aria-hidden="true">📹</span> Iniciar videollamada
                </a>
              </motion.div>
              {/* COLUMNA DERECHA — Imagen */}
              <motion.div
                className="md:order-2"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <img
                  src="https://readdy.ai/api/search-image?query=A%20real%20professional%20mosquito%20screen%20technician%20showing%20a%20damaged%20mosquito%20net%20frame%20to%20a%20client%20through%20a%20WhatsApp%20video%20call%20on%20a%20smartphone%20screen%2C%20Mediterranean%20home%20interior%20visible%20in%20the%20background%2C%20warm%20natural%20daylight%2C%20authentic%20professional%20service%20photography%2C%20detailed%20hands%20and%20tools%20visible%2C%20shallow%20depth%20of%20field&width=600&height=500&seq=mosquiteras-hub-videocall&orientation=landscape&format=webp"
                  alt="Técnico de mosquiteras en videollamada WhatsApp gratis para diagnóstico"
                  loading="lazy"
                  className="w-full h-auto object-cover rounded-2xl"
                  style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                  width="600"
                  height="500"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* FORM */}
        <div id="presupuesto">
          <QuoteFormSection />
        </div>
      </main>
      <Footer />
      <WhatsAppFloating />
      <CookieBanner />
    </div>
  );
}